import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { PaymentMethod } from "src/app/models/payment-method";
import { CompanyParamsService } from "src/app/services/companyParams/company-params.service";
import { PaymentMethodTypeEnum } from "src/app/enums/payment-methods-type.enum-";
import { HttpRequestPlacetopayService } from "./http-request-placetopay.service";
import { environment } from "src/environments/environment";
import { PaymentOption } from "../commons/parameters";

// Interface para el request de creación de sesión PlaceToPay
export interface PlacetopaySessionRequest {
  locale: string;
  payment: {
    reference: string;
    description: string;
    amount: {
      currency: string;
      total: number;
    };
    allowPartial: boolean;
    subscribe: boolean;
  };
  returnUrl: string;
  ipAddress: string;
  userAgent: string;
  expiration: string;
}

// Interfaces para crear sesión de pago
export interface PlacetopayPayer {
  documentType: string;
  documentNumber: string;
  name: string;
  surname: string;
  email: string;
  mobile: string;
}

export interface PlacetopayCreateSessionRequest {
  reference: string;
  description: string;
  amount: number;
  currency: string;
  returnUrl: string;
  cancelUrl: string;
  payer: PlacetopayPayer;
  keyCompany: any;
  ipAddress: string;
}

export interface PlacetopayCreateSessionResponse {
  requestId: string;
  processUrl: string;
  status: PlacetopayStatus;
}

export interface PlacetopayStatus {
  status: string;
  reason: string;
  message: string;
  date: string;
}

export interface PlacetopayPaymentAmount {
  from?: { total?: number; currency?: string };
  to?: { total?: number; currency?: string };
  factor?: number;
}

export interface PlacetopayPayment {
  // El estado del intento de pago es un objeto (status/reason/message/date),
  // no un string: la sesion puede seguir PENDING mientras el intento ya fue REJECTED.
  status: PlacetopayStatus;
  amount?: PlacetopayPaymentAmount;
  reference?: string;
  internalReference?: number | string;
  authorization?: string;
  receipt?: string;
  refunded?: boolean;
  franchise?: string;
  issuerName?: string;
  paymentMethod?: string;
  paymentMethodName?: string;
  processorFields?: any[];
}

export interface PlacetopaySessionResponse {
  requestId: string;
  status: PlacetopayStatus;
  reference: string;
  amount: number;
  currency: string;
  requestDate: string;
  lastUpdate: string;
  // PlaceToPay devuelve un arreglo con los intentos de pago de la sesion
  // (puede venir vacio o nulo cuando aun no existe informacion de pago).
  payment: PlacetopayPayment[];
  request: PlacetopaySessionRequest;
  subscription?: any;
}

// Origen del estado efectivo resuelto a partir de la respuesta de la sesion.
export type PlacetopayStatusSource = 'PAYMENT' | 'SESSION';

export interface PlacetopayProcessResult {
  data?: PlacetopaySessionResponse;
  // Estado efectivo: payment[0].status si existe informacion de pago, si no status de la sesion.
  status?: PlacetopayStatus;
  statusSource?: PlacetopayStatusSource;
  error?: any;
}

// Interface para confirmar pago PlaceToPay
export interface PlacetopayConfirmPaymentRequest {
  itinerary: number;
  jsonOnHold: string;
  paymentID: number;
  jsonSendConfirmationMail?: String;
  jsonSendInquireRequestEmail?: String;
}

export interface PlacetopayConfirmPaymentResponse {
  status: boolean;
  message: any;
  pedido?: number;
}

export interface TransactionData {
  reference: string;
  dateTime: string;
  status: string;
  amount: number;
  currency: string;
  authorization: string;
}

// Enum para estados de pago estandarizados
export enum PlacetopayPaymentStatus {
  APPROVED = "APPROVED",
  PENDING = "PENDING",
  REJECTED = "REJECTED",
  FAILED = "FAILED",
  ERROR = "ERROR",
}

export enum PlacetopayInvocationContext {
  CHECKOUT = "CHECKOUT",
  LIGHTBOX = "LIGHTBOX",
}

@Injectable({
  providedIn: "root",
})
export class PlacetopayService {
  private readonly sessionEndpointBase = environment.PLACETOPAY_BACKEND_SESSION;

  constructor(
    private http: HttpClient,
    public companyParamsService: CompanyParamsService,
    private httpRequestPlacetopayService: HttpRequestPlacetopayService
  ) {}


  /**
   * Obtener el metodo de pago de PLACETOPAY
   * @param company empresa Ejm. companyParamsService.company.V
   * @returns PaymentMethod
   */
  paymentMethodPlacetopay = (): PaymentMethod | undefined => {
    try {
      const index = this.companyParamsService.company.paymentMethods.findIndex(
        (item) => Number(item.type) === Number(PaymentMethodTypeEnum.PLACETOPAY)
      );
      return index || index === 0
        ? this.companyParamsService.company.paymentMethods[index]
        : undefined;
    } catch (error) {}
  };

  /**
   * Determina si se debe mostrar el estado de carga para PlaceToPay.
   * Similar a stripeService.loading() - retorna true cuando el paymentOption es PLACETOPAY.
   * @param paymentOption Opción de pago actual
   * @returns boolean indicando si se debe mostrar loading
   */
  loading = (paymentOption: PaymentOption): boolean => {
    if (paymentOption === PaymentOption.NONE) {
      return false;
    }
    if (paymentOption === PaymentOption.PLACETOPAY) {
      return true;
    }
    return false;
  };

  /**
   * Resuelve el estado efectivo de una sesion de PlaceToPay.
   * Regla de consulta:
   *   1. Primero verificar si existe informacion de pago (payment no nulo y no vacio).
   *   2. Si existe pago, el estado se obtiene de payment[0].status.status.
   *   3. Si no existe pago, el estado se obtiene del objeto status de la sesion.
   * Nota: TypeScript 3.2 -> se usan guardas con && (sin optional chaining).
   * @param session Respuesta de la sesion de PlaceToPay
   * @returns Estado efectivo o null si la respuesta no es utilizable
   */
  resolveEffectiveStatus(session: PlacetopaySessionResponse): PlacetopayStatus {
    const paymentStatus = this.getPaymentStatus(session);
    if (paymentStatus) {
      return paymentStatus;
    }
    return session && session.status ? session.status : null;
  }

  /**
   * Obtiene el estado del intento de pago cuando existe informacion de pago.
   * @param session Respuesta de la sesion de PlaceToPay
   * @returns Estado del pago o null si no hay informacion de pago utilizable
   */
  private getPaymentStatus(session: PlacetopaySessionResponse): PlacetopayStatus {
    if (!session || !session.payment || !session.payment.length) {
      return null;
    }
    const payment = session.payment[0];
    if (payment && payment.status && payment.status.status) {
      return payment.status;
    }
    return null;
  }

  /**
   * Indica de donde se tomo el estado efectivo (para logs y soporte).
   * @param session Respuesta de la sesion de PlaceToPay
   */
  getStatusSource(session: PlacetopaySessionResponse): PlacetopayStatusSource {
    return this.getPaymentStatus(session) ? 'PAYMENT' : 'SESSION';
  }

  /**
   * Consulta el estado de un pago en PlacetoPay.
   * Este método solo verifica el estado del pago, no ejecuta lógica de negocio.
   * @param requestId ID de la sesión de pago
   * @param keyCompany
   * @returns Resultado estandarizado del estado del pago
   */
  processPaymentPlacetopay = async (
    keyCompany: string,
    requestId: string
  ): Promise<PlacetopayProcessResult> => {
    return new Promise<PlacetopayProcessResult>((resolve) => {
      const url = `${this.sessionEndpointBase}${keyCompany}/${requestId}`;
      console.log("PlacetoPay: Consultando estado de pago, URL:", url);
      this.http.get<PlacetopaySessionResponse>(url).subscribe(
        (data) => {
          console.log("PlacetoPay: Respuesta recibida:", data);
          const effectiveStatus = this.resolveEffectiveStatus(data);
          const statusSource = this.getStatusSource(data);
          console.log(
            "PlacetoPay: Estado efectivo resuelto:",
            effectiveStatus ? effectiveStatus.status : null,
            "origen:",
            statusSource
          );

          resolve({
            data,
            status: effectiveStatus,
            statusSource,
          });
        },
        (error: any) => {
          console.error("PlacetoPay: Error en la petición:", error);
          resolve({

            error,
          });
        }
      );
    });
  };



  /**
   * Crea una nueva sesión de pago en PlacetoPay.
   * @param request Datos de la sesión de pago a crear
   * @returns Observable con la respuesta de creación de sesión
   */
  createPaymentSession(
    request: PlacetopayCreateSessionRequest
  ): Observable<PlacetopayCreateSessionResponse> {
    console.log("PlacetoPay: Creando sesión de pago:", request);
    return this.http.post<PlacetopayCreateSessionResponse>(
      this.sessionEndpointBase,
      request
    );
  }

  /**
   * Crea una sesión de pago y retorna una promesa con el resultado.
   * @param request Datos de la sesión de pago a crear
   * @returns Promesa con la URL de redirección o error
   */
  createPaymentSessionAsync = async (
    request: PlacetopayCreateSessionRequest
  ): Promise<{
    success: boolean;
    processUrl?: string;
    requestId?: string;
    error?: any;
  }> => {
    return new Promise((resolve) => {
      this.createPaymentSession(request).subscribe(
        (response) => {
          console.log("PlacetoPay: Sesión creada exitosamente:", response);
          resolve({
            success: true,
            processUrl: response.processUrl,
            requestId: response.requestId,
          });
        },
        (error: any) => {
          console.error("PlacetoPay: Error al crear sesión:", error);
          resolve({ success: false, error });
        }
      );
    });
  };

  /**
   * Bloqueo temporal hasta que realice el pago .
   * Este método llama al endpoint statusOnHoldPlaceToPay bloquea temporalmente la cabina  hasta realizar el pago.
   * Envía el token de autenticación en la cabecera Authorization.
   * @param itinerary Código del itinerario
   * @param jsonOnHold JSON con los datos del bloqueo de cabina (generado por jsonblockPurchaseCabin)
   * @param paymentID ID del pago de PlaceToPay (requestId de la sesión)
   * @returns Observable con la respuesta de confirmación
   */
  statusPlacetopayPayment(
    itinerary: number,
    jsonOnHold: any,
    paymentID: number,
    jsonSendConfirmationMail?: any,
    jsonSendInquireRequestEmail?: any
  ): Observable<PlacetopayConfirmPaymentResponse> {
    const body: PlacetopayConfirmPaymentRequest & { transactionData?: TransactionData } = {
      itinerary: itinerary,
      jsonOnHold: JSON.stringify(jsonOnHold),
      paymentID: paymentID,
      jsonSendConfirmationMail: JSON.stringify(jsonSendConfirmationMail),
      jsonSendInquireRequestEmail: JSON.stringify(jsonSendInquireRequestEmail),
    };

    console.log("PlaceToPay: Confirmando pago con statusOnHoldPlaceToPay");
    console.log("PlaceToPay: Body de confirmación:", body);
    return this.httpRequestPlacetopayService.requestPost('statusOnHoldPlaceToPay', body);
  }

  /**
   * Confirma el pago PlaceToPay de forma asíncrona.
   * @param itinerary Código del itinerario
   * @param jsonOnHold Objeto con los datos del bloqueo de cabina
   * @param paymentID ID del pago de PlaceToPay
   * @returns Promesa con el resultado de la confirmación
   */
  statusPlacetopayPaymentAsync = async (
    itinerary: number,
    jsonOnHold: any,
    paymentID: number,
    jsonSendConfirmationMail?: any,
    jsonSendInquireRequestEmail?: any
  ): Promise<{
    success: boolean;
    data?: PlacetopayConfirmPaymentResponse;
    error?: any;
  }> => {
    return new Promise((resolve) => {
      this.statusPlacetopayPayment(itinerary, jsonOnHold, paymentID,jsonSendConfirmationMail, jsonSendInquireRequestEmail).subscribe(
        (response) => {
          console.log("PlaceToPay: Pago confirmado exitosamente:", response);
          resolve({
            success: response.status,
            data: response,
          });
        },
        (error: any) => {
          console.error("PlaceToPay: Error al confirmar pago:", error);
          resolve({ success: false, error });
        }
      );
    });
  };

  /**
   * Cancela el bloqueo de cabina (status on hold) para PlaceToPay.
   * Este método se llama cuando el pago es rechazado o cancelado.
   * @param transaction Código de la transacción a cancelar
   * @returns Observable con la respuesta de cancelación
   */
  cancelStatusOnHoldPlacetopay(
    transaction: number
  ): Observable<PlacetopayConfirmPaymentResponse> {
    const body = {
      transaction: transaction,
    };

    console.log("PlaceToPay: Cancelando status on hold con cancelStatusOnHoldPlaceToPay");
    console.log("PlaceToPay: Body de cancelación:", body);
    return this.httpRequestPlacetopayService.requestPost('cancelStatusOnHoldPlaceToPay', body);
  }

  /**
   * Cancela el bloqueo de cabina (status on hold) para PlaceToPay de forma asíncrona.
   * @param transaction Código de la transacción a cancelar
   * @returns Promesa con el resultado de la cancelación
   */
  cancelStatusOnHoldPlacetopayAsync = async (
    transaction: number
  ): Promise<{
    success: boolean;
    data?: PlacetopayConfirmPaymentResponse;
    error?: any;
  }> => {
    return new Promise((resolve) => {
      this.cancelStatusOnHoldPlacetopay(transaction).subscribe(
        (response) => {
          console.log("PlaceToPay: Status on hold cancelado exitosamente:", response);
          resolve({
            success: response.status,
            data: response,
          });
        },
        (error: any) => {
          console.error("PlaceToPay: Error al cancelar status on hold:", error);
          resolve({ success: false, error });
        }
      );
    });
  };

  /**
   * Metodo que se encarga de obtener el error cuando existe el pago pero no se pudo bloquear la cabina.
   * @param error Mensaje de error
   */
  public onHoldErrorMessage(email:string):string {
    let errorText =
        `Booking Failed: We apologize, but we were unable to complete your booking at this time.
        Your transaction was not finalized correctly.
        To secure your cabin, we recommend refreshing the page or trying again in a few minutes.
        If the issue persists, please contact our support team at (
        ${email})`;
    return errorText;
  }
}
